import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpDown, BriefcaseBusiness, Globe, Mail, MapPin, Phone, Plus, RefreshCw, Search, ShieldCheck, WalletCards, X } from "lucide-react";
import { useAuth } from "context/auth";
import { getCardholdersV2 } from "./api";
import AdminPassPreview from "../components/AdminPassPreview";
import StatusBadge from "../components/StatusBadge";

const API_URL = process.env.REACT_APP_API_URL || "";
const uploadsBaseUrl = API_URL.replace(/\/api\/?$/, "").replace(/\/$/, "");

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

function resolveCardAsset(value) {
  if (!value || typeof value !== "string") return "";
  if (
    value.startsWith("data:") ||
    value.startsWith("blob:") ||
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return value;
  }
  if (value.startsWith("/uploads/")) {
    return `${uploadsBaseUrl}${value}`;
  }
  if (value.includes("/uploads/")) {
    return `${uploadsBaseUrl}/uploads/${value.split("/uploads/").pop()}`.replace(/([^:]\/)\/+/g, "$1");
  }
  return `${uploadsBaseUrl}/uploads/${value}`;
}

function CardholderThumb({ cardholder }) {
  const iconSrc = resolveCardAsset(cardholder?.logoImage);
  const fallbackLetter = (cardholder?.name || "C").trim().charAt(0).toUpperCase();

  return (
    <div
      className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]"
      style={{ backgroundColor: cardholder?.backgroundColor || "#2C3255" }}
    >
      <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-[10px] tw-bg-white/12 tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] tw-backdrop-blur-[2px]">
        {iconSrc ? (
          <img src={iconSrc} alt="" className="tw-h-full tw-w-full tw-object-contain" />
        ) : (
          <span className="tw-text-sm tw-font-semibold tw-text-white">{fallbackLetter}</span>
        )}
      </div>
    </div>
  );
}

function CardholderDetailDrawer({ cardholder, open, onClose, onEdit }) {
  if (!open || !cardholder) return null;

  const preview = {
    passType: cardholder.passType || "APPLE",
    design: {
      backgroundColor: cardholder.backgroundColor || "#2C3255",
      stripImage: cardholder.stripImage || "",
      logoImage: cardholder.logoImage || "",
      prefix: cardholder.prefix || "",
      firstName: cardholder.name?.split(" ")?.[0] || "Cardly",
      lastName: cardholder.name?.split(" ")?.slice(1)?.join(" ") || "User",
      fullName: cardholder.name || "Cardly User",
      jobTitle: cardholder.title || "",
      email: cardholder.email || "",
      phone: cardholder.phone || "",
      phones: Array.isArray(cardholder.phones) ? cardholder.phones : [],
      addresses: Array.isArray(cardholder.addresses) ? cardholder.addresses : [],
      organisationName: cardholder.organization || "Cardly",
      companyURL: cardholder.companyURL || "",
      linkedInURL: cardholder.linkedInURL || "",
    },
    values: {
      prefix: cardholder.prefix || "",
    },
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-justify-end">
      <button type="button" className="tw-absolute tw-inset-0 tw-bg-[rgba(20,26,44,0.28)]" onClick={onClose} />
      <aside className="admin-v2-soft-panel tw-relative tw-z-[1] tw-h-full tw-w-full tw-max-w-[720px] tw-overflow-y-auto tw-rounded-none tw-border-l tw-border-[rgba(31,41,64,0.08)] tw-p-4 sm:tw-p-5 lg:tw-p-6">
        <div className="tw-mb-5 tw-flex tw-items-start tw-justify-between tw-gap-4">
          <div>
            <p className="admin-v2-eyebrow">Cardholder detail</p>
            <h2 className="tw-mt-2 tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)] sm:tw-text-2xl">{cardholder.name}</h2>
            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{cardholder.email || "-"}</p>
          </div>
          <button type="button" onClick={onClose} className="tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-text-[var(--admin-v2-ink)]">
            <X size={18} />
          </button>
        </div>

        <div className="tw-space-y-5">
          <div className="tw-grid tw-gap-4 xl:tw-grid-cols-[minmax(0,1fr)_280px]">
            <div>
              <p className="admin-v2-eyebrow">Profile</p>
              <p className="tw-mt-2 tw-text-2xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">{cardholder.organization || "Independent workspace"}</p>
              <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{cardholder.title || "No title assigned"}</p>
            </div>
            <div className="tw-flex tw-items-start tw-justify-center xl:tw-justify-end">
              <AdminPassPreview preview={preview} />
            </div>
          </div>

          <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
            <section className="tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
              <div className="tw-flex tw-items-center tw-gap-2"><Mail size={16} className="tw-text-[var(--admin-v2-accent)]" /><p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">Contact</p></div>
              <div className="tw-mt-4 tw-space-y-3">
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3"><p className="admin-v2-eyebrow tw-mb-1">Primary email</p><p className="tw-break-words tw-whitespace-normal tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{cardholder.email || "-"}</p></div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3"><p className="admin-v2-eyebrow tw-mb-1">Extra emails</p><p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{Array.isArray(cardholder.emails) ? cardholder.emails.length : 0}</p></div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3"><p className="admin-v2-eyebrow tw-mb-1">Phones</p><p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{Array.isArray(cardholder.phones) ? cardholder.phones.length : 0}</p></div>
              </div>
            </section>

            <section className="tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
              <div className="tw-flex tw-items-center tw-gap-2"><Globe size={16} className="tw-text-[var(--admin-v2-accent)]" /><p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">Workspace links</p></div>
              <div className="tw-mt-4 tw-space-y-3">
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3"><p className="admin-v2-eyebrow tw-mb-1">Company URL</p><p className="tw-break-all tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{cardholder.companyURL || "-"}</p></div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3"><p className="admin-v2-eyebrow tw-mb-1">LinkedIn</p><p className="tw-break-all tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{cardholder.linkedInURL || "-"}</p></div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3"><p className="admin-v2-eyebrow tw-mb-1">Created</p><p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{formatDate(cardholder.createdAt)}</p></div>
              </div>
            </section>
          </div>

          <section className="tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
            <div className="tw-flex tw-items-center tw-gap-2"><MapPin size={16} className="tw-text-[var(--admin-v2-accent)]" /><p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">Addresses and status</p></div>
            <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
              <StatusBadge status={cardholder.disable ? "Disabled" : cardholder.status || "Active"} />
              <StatusBadge status={cardholder.organization || "No workspace"} />
            </div>
            <div className="tw-mt-4 tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
              <p className="admin-v2-eyebrow tw-mb-1">Addresses</p>
              <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">{Array.isArray(cardholder.addresses) ? cardholder.addresses.length : 0}</p>
            </div>
          </section>

          <div className="tw-flex tw-justify-end">
            <button onClick={() => onEdit(cardholder)} className="admin-v2-action admin-v2-action-primary" type="button">
              Edit Cardholder
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default function AdminV2Cardholders() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cardholders, setCardholders] = useState([]);
  const [error, setError] = useState("");
  const [selectedCardholderId, setSelectedCardholderId] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  const fetchCardholders = async () => {
    try {
      setError("");
      setLoading(true);
      const data = await getCardholdersV2();
      setCardholders(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error fetching cardholders:", e);
      setError("Failed to load cardholders.");
      setCardholders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser?.role) return;
    fetchCardholders();
  }, [currentUser?.role]);

  const selectedCardholder = useMemo(
    () => cardholders.find((item) => String(item?._id) === String(selectedCardholderId)) || null,
    [cardholders, selectedCardholderId]
  );

  const filteredCardholders = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return cardholders.filter((item) => {
      const matchesQuery =
        !normalizedQuery ||
        [item?.name, item?.email, item?.organization, item?.title]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));

      const status = item?.disable ? "disabled" : "active";
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesQuery && matchesStatus;
    });
  }, [cardholders, query, statusFilter]);

  const sortedCardholders = useMemo(() => {
    const factor = sortDirection === "asc" ? 1 : -1;

    return [...filteredCardholders].sort((left, right) => {
      const getValue = (item) => {
        switch (sortKey) {
          case "workspace":
            return item?.organization || "";
          case "contact":
            return item?.email || item?.phone || "";
          case "status":
            return item?.disable ? 1 : 0;
          case "created":
            return new Date(item?.createdAt || 0).getTime();
          case "name":
          default:
            return item?.name || "";
        }
      };

      const leftValue = getValue(left);
      const rightValue = getValue(right);

      if (typeof leftValue === "number" || typeof rightValue === "number") {
        return ((Number(leftValue) || 0) - (Number(rightValue) || 0)) * factor;
      }

      return String(leftValue).localeCompare(String(rightValue), undefined, {
        numeric: true,
        sensitivity: "base",
      }) * factor;
    });
  }, [filteredCardholders, sortDirection, sortKey]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDirection("asc");
  };

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-6 lg:tw-p-8">
        <div className="tw-flex tw-flex-col tw-gap-5 lg:tw-flex-row lg:tw-items-end lg:tw-justify-between">
          <div className="tw-max-w-2xl tw-space-y-3">
            <p className="admin-v2-eyebrow">Cardholders</p>
            <h1 className="admin-v2-title">Manage Cardholders.</h1>
            <p className="admin-v2-subtitle">
              Manage people, passes, and invite flow in one calmer surface.
            </p>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-3">
            <button onClick={fetchCardholders} className="admin-v2-action admin-v2-action-secondary" type="button">
              <RefreshCw size={15} />
              Refresh
            </button>
            <button onClick={() => navigate("/admin-v2/cardholders/create")} className="admin-v2-action admin-v2-action-primary" type="button">
              <Plus size={16} />
              Create Pass
            </button>
          </div>
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-grid tw-gap-4 xl:tw-grid-cols-[180px_180px_180px_180px_minmax(0,1fr)]">
          <div className="admin-v2-stat-panel">
            <p className="admin-v2-eyebrow tw-mb-3">Total</p>
            <p className="tw-text-3xl tw-font-semibold admin-v2-text-strong">{cardholders.length}</p>
          </div>
          <div className="admin-v2-stat-panel">
            <p className="admin-v2-eyebrow tw-mb-3">Status</p>
            <p className="tw-text-base tw-font-medium admin-v2-text-strong">
              {loading ? "Syncing..." : error ? "Attention needed" : "Ready"}
            </p>
          </div>
          <div className="admin-v2-stat-panel">
            <p className="admin-v2-eyebrow tw-mb-3">Active</p>
            <p className="tw-text-3xl tw-font-semibold admin-v2-text-strong">
              {cardholders.filter((item) => !item?.disable).length}
            </p>
          </div>
          <div className="admin-v2-stat-panel">
            <p className="admin-v2-eyebrow tw-mb-3">Disabled</p>
            <p className="tw-text-3xl tw-font-semibold admin-v2-text-strong">
              {cardholders.filter((item) => item?.disable).length}
            </p>
          </div>
          {/* <div className="admin-v2-brand-band tw-rounded-[22px] tw-p-4 xl:tw-col-span-5">
            <p className="admin-v2-eyebrow tw-mb-2">Action lane</p>
            <p className="tw-text-sm tw-leading-6 admin-v2-text-muted">
              Start from create pass when onboarding a new person, or open an existing record below to refresh their details and branding.
            </p>
          </div> */}
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
          <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 sm:tw-flex-row">
            <label className="admin-v2-filter-field sm:tw-max-w-[420px] tw-w-full">
              <span className="admin-v2-eyebrow">Search cardholders</span>
              <span className="admin-v2-filter-input-wrap">
                <Search size={16} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by name, email, title, workspace..."
                />
              </span>
            </label>

            <label className="admin-v2-filter-field sm:tw-max-w-[220px] tw-w-full">
              <span className="admin-v2-eyebrow">Status</span>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="admin-v2-filter-select"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
          </div>

          <div className="admin-v2-brand-badge">
            <span className="admin-v2-brand-dot" />
            {sortedCardholders.length} cardholder records
          </div>
        </div>
      </section>

      <section className="admin-v2-table-wrap">
        {loading ? (
          <div className="tw-p-8 admin-v2-text-muted">Loading cardholders...</div>
        ) : error ? (
          <div className="admin-v2-status-note tw-m-3 tw-p-4 tw-text-sm">{error}</div>
        ) : sortedCardholders?.length === 0 ? (
          <div className="tw-p-8 admin-v2-text-muted">No cardholders found.</div>
        ) : (
          <div className="tw-p-3 lg:tw-p-4">
            <div className="tw-hidden lg:tw-grid tw-grid-cols-[minmax(0,2fr)_minmax(0,1.15fr)_minmax(0,1.1fr)_140px_120px] tw-gap-4 tw-px-4 tw-py-3 tw-text-[11px] tw-uppercase tw-tracking-[0.18em] admin-v2-text-muted">
              {[
                ["name", "Cardholder"],
                ["contact", "Contact"],
                ["workspace", "Workspace"],
                ["status", "Status"],
              ].map(([key, label]) => (
                <button key={key} type="button" onClick={() => handleSort(key)} className="admin-v2-sort-button tw-justify-start">
                  {label}
                  <ArrowUpDown size={13} />
                </button>
              ))}
              <div className="tw-text-right">Action</div>
            </div>

            <div className="tw-space-y-3">
              {sortedCardholders.map((ch) => (
                <div
                  key={ch?._id}
                  onClick={() => {
                    setSelectedCardholderId(ch?._id);
                    setDetailOpen(true);
                  }}
                  className="tw-rounded-[22px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4 lg:tw-p-5 tw-shadow-[0_12px_24px_rgba(15,23,42,0.04)]"
                >
                  <div className="tw-flex tw-flex-col tw-gap-4 lg:tw-grid lg:tw-grid-cols-[minmax(0,2fr)_minmax(0,1.15fr)_minmax(0,1.1fr)_140px_120px] lg:tw-items-center lg:tw-gap-4">
                    <div className="tw-flex tw-items-start tw-gap-4">
                      <CardholderThumb cardholder={ch} />
                      <div className="tw-space-y-1">
                        <div className="tw-font-semibold admin-v2-text-strong">{ch?.name || "-"}</div>
                        <div className="tw-text-sm admin-v2-text-muted">
                          {ch?.title || "No title"} {ch?.organization ? `- ${ch.organization}` : ""}
                        </div>
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                          <span className="admin-v2-inline-meta">
                            <WalletCards size={13} />
                            Digital pass
                          </span>
                          <span className="admin-v2-inline-meta">
                            <ShieldCheck size={13} />
                            {ch?.disable ? "Needs action" : "Ready"}
                          </span>
                          <span className="admin-v2-inline-meta">{formatDate(ch?.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="tw-space-y-2 tw-text-sm admin-v2-text-muted">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <Mail size={14} className="tw-text-[var(--admin-v2-accent)]" />
                        <span className="tw-truncate">{ch?.email || "-"}</span>
                      </div>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <Phone size={14} className="tw-text-[var(--admin-v2-accent)]" />
                        <span>{ch?.phone || "No phone added"}</span>
                      </div>
                    </div>

                    <div className="tw-space-y-2 tw-text-sm">
                      <div className="tw-flex tw-items-center tw-gap-2 admin-v2-text-muted">
                        <BriefcaseBusiness size={14} className="tw-text-[var(--admin-v2-accent)]" />
                        <span className="tw-font-medium tw-text-[var(--admin-v2-ink)]">
                          {ch?.organization || "No workspace linked"}
                        </span>
                      </div>
                      <p className="tw-text-xs admin-v2-text-muted">
                        {ch?.title || "Role not assigned"}
                      </p>
                    </div>

                    <div>
                      <span
                        className={`tw-inline-flex tw-items-center tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-medium ${
                          ch?.disable ? "tw-bg-[rgba(243,86,88,0.12)] tw-text-[#C44749]" : "tw-bg-[rgba(250,216,69,0.18)] tw-text-[#2C3255]"
                        }`}
                      >
                        {ch?.disable ? "Disabled" : ch?.status || "Active"}
                      </span>
                    </div>

                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate("/admin-v2/cardholders/create", { state: ch });
                      }}
                      className="tw-inline-flex tw-items-center tw-justify-end tw-gap-2 tw-text-sm tw-font-medium admin-v2-text-strong hover:tw-text-[#F35658] lg:tw-justify-self-end"
                      type="button"
                    >
                      Edit
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <CardholderDetailDrawer
        cardholder={selectedCardholder}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onEdit={(cardholder) => navigate("/admin-v2/cardholders/create", { state: cardholder })}
      />
    </div>
  );
}
